import React, { useRef, useState } from "react";
import { Paperclip, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const ACCEPTED_MIMES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_EXT = ".jpg,.jpeg,.png,.webp";
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB per file

// Read a File → pure base64 string (no data: prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = () => reject(r.error || new Error("read failed"));
    r.readAsDataURL(file);
  });
}

/**
 * AttachmentPicker — compact image picker for chat & Orion composers.
 *
 * Props:
 *   attachments: [{ id, mime, data_b64, preview_url, name, size }]
 *   setAttachments: setState fn
 *   max: int (default 3)
 *   disabled: bool
 *   variant: "chat" | "orion"  — tweaks label copy
 *   testId: string — root data-testid prefix
 *   compact: bool — render an icon-only button (fits inside a chat bar)
 *   hideThumbs: bool — skip the thumbnail strip (caller renders them)
 *   hideButton: bool — skip the add button (caller already has one)
 */
export default function AttachmentPicker({
  attachments,
  setAttachments,
  max = 3,
  disabled = false,
  variant = "chat",
  testId = "attachment-picker",
  compact = false,
  hideThumbs = false,
  hideButton = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const slotsLeft = Math.max(0, max - attachments.length);
    if (slotsLeft === 0) {
      toast.error(`Max ${max} images per message.`);
      return;
    }
    const next = [];
    for (const f of files.slice(0, slotsLeft)) {
      if (!ACCEPTED_MIMES.includes(f.type)) {
        toast.error(`"${f.name}" — use JPEG, PNG, or WEBP only.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" is over 8 MB — try a smaller copy.`);
        continue;
      }
      try {
        const data_b64 = await fileToBase64(f);
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          mime: f.type,
          data_b64,
          preview_url: URL.createObjectURL(f),
          name: f.name,
          size: f.size,
        });
      } catch (err) {
        console.warn("[AttachmentPicker] read failed", err);
        toast.error(`Couldn't read "${f.name}".`);
      }
    }
    if (next.length > 0) setAttachments((prev) => [...prev, ...next]);
  };

  const removeAt = (idx) => {
    setAttachments((prev) => {
      const victim = prev[idx];
      if (victim?.preview_url) {
        try { URL.revokeObjectURL(victim.preview_url); } catch { /* noop */ }
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer?.files?.length) {
      await addFiles(e.dataTransfer.files);
    }
  };

  // Paste-from-clipboard support when focused siblings forward items here.
  // Consumers can call handlePaste(event) to adopt this behavior.
  const handlePaste = async (e) => {
    if (disabled) return;
    const items = e.clipboardData?.items || [];
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      await addFiles(files);
    }
  };

  const label = variant === "orion" ? "Reference image" : "Attach image";

  return (
    <div
      className={compact ? "inline-block" : "w-full"}
      data-testid={`${testId}-root`}
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onPaste={handlePaste}
    >
      {!hideThumbs && attachments.length > 0 && (
        <div
          className="flex flex-wrap gap-2 mb-2"
          data-testid={`${testId}-thumbs`}
        >
          {attachments.map((a, i) => (
            <div
              key={a.id}
              className="relative border border-zinc-800 bg-[#0b0b0d] p-1 group"
              data-testid={`${testId}-thumb-${i}`}
            >
              <img
                src={a.preview_url}
                alt={a.name}
                className="h-16 w-16 object-cover"
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={disabled}
                data-testid={`${testId}-remove-${i}`}
                className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center border border-zinc-700 bg-zinc-950 text-zinc-400 hover:border-rose-500 hover:text-rose-400 transition-colors"
                aria-label={`Remove ${a.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!hideButton && (
        <div className={compact ? "" : "flex items-center gap-2 flex-wrap"}>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || attachments.length >= max}
            data-testid={`${testId}-btn`}
            aria-label={label}
            title={
              attachments.length >= max
                ? `Max ${max} images`
                : `${label} · JPEG / PNG / WEBP · up to ${max}, 8 MB each`
            }
            className={[
              compact
                ? "inline-flex items-center justify-center h-8 w-8 border relative"
                : "inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.3em] border",
              "transition-colors",
              disabled || attachments.length >= max
                ? "border-zinc-800 text-zinc-700 cursor-not-allowed"
                : "border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400",
              dragOver ? "border-emerald-500 text-emerald-400" : "",
            ].join(" ")}
          >
            {variant === "orion" ? (
              <ImageIcon className="h-3.5 w-3.5" />
            ) : (
              <Paperclip className="h-3.5 w-3.5" />
            )}
            {!compact && label}
            {!compact && attachments.length > 0 && ` · ${attachments.length}/${max}`}
            {compact && attachments.length > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-emerald-500 bg-[#09090b] text-emerald-400 rounded-sm"
                data-testid={`${testId}-count`}
              >
                {attachments.length}
              </span>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXT}
            multiple
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
            disabled={disabled}
          />
          {!compact && dragOver && (
            <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">
              Drop to attach
            </span>
          )}
        </div>
      )}
    </div>
  );
}
